<template>
    <div class="member-edit guided-steps-page">
        <div class="login-sso-notice">
            <p class="login-sso-notice__title">
                <i class="el-icon-info login-sso-notice__icon" aria-hidden="true"></i>
                AIユイゴン&nbsp;Well-Bをご利用の方へ
            </p>
            <p class="login-sso-notice__text">
                ログイン画面からAIユイゴン&nbsp;Well-Bのログイン情報にてログインしてください。
            </p>
        </div>
        <el-form class="form mt2" ref="memberChangeLoginId" :model="form" :rules="rules">
            <!-- Guided steps (backlog #17, same behaviour as 本人確認 #13): ① the contact fields and
                 the resulting login ID, ending with the send button, which first checks ①'s
                 fields; ② the code + submit, dimmed AND locked until a code is sent in this page
                 visit. The server flow is unchanged. -->
            <div class="iv-steps">
                <section :class="['iv-step', codeSent ? 'is-done' : 'is-active']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">
                            <i v-if="codeSent" class="el-icon-check" aria-hidden="true"></i>
                            <template v-else>1</template>
                        </span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">新しいログインIDを入力</h2>
                        <table class="table-register">
                            <tr>
                                <th id="email" :class="['column-label', { required: isEmailLoginIdRequired }]">メールアドレス</th>
                                <td class="column-input max-width-400">
                                    <el-form-item prop="email">
                                        <el-input class="input" v-model="form.email" :disabled="disabled" placeholder="xxxxxxx@example.com"></el-input>
                                        <span class="d-inline-block small-text">半角英数字で入力してください</span>
                                    </el-form-item>
                                </td>
                            </tr>
                            <tr>
                                <th id="mobile-phone" :class="['column-label', { required: isMobileLoginIdRequired }]">携帯電話番号</th>
                                <td class="column-input max-width-400">
                                    <el-form-item prop="mobilePhone">
                                        <el-input class="input" v-model="form.mobilePhone" :disabled="disabled" placeholder="070-xxxx-xxxx"></el-input>
                                        <span class="d-inline-block small-text">半角数字（ハイフンあり）で入力してください</span>
                                    </el-form-item>
                                </td>
                            </tr>
                            <tr>
                                <th id="new-login-id" class="column-label">新しいログインID</th>
                                <td class="column-input max-width-400">
                                    <el-form-item>
                                        <el-input class="input login-id-display" :value="newLoginIdDisplay" readonly disabled placeholder="上記より選択してください"></el-input>
                                        <span class="d-inline-block small-text">{{ loginIdTypeLabel }}がログインIDとして使用されます</span>
                                    </el-form-item>
                                </td>
                            </tr>
                        </table>
                        <div class="iv-step__action">
                            <el-button type="primary" class="send-code-button" @click="handleSendVerificationCode" :disabled="disabled || verificationCodeCooldown > 0">本人確認コードを送信</el-button>
                            <span v-if="verificationCodeCooldown > 0" class="d-inline-block small-text">再送信可能まで30秒</span>
                        </div>
                    </div>
                </section>
                <section :class="['iv-step', codeSent ? 'is-active' : 'is-pending']">
                    <div class="iv-step__marker">
                        <span class="iv-step__number">2</span>
                    </div>
                    <div class="iv-step__body">
                        <h2 class="iv-step__title">本人確認コードを入力</h2>
                        <table class="table-register">
                            <tr>
                                <th id="verification-code" class="column-label required">本人確認コード</th>
                                <td class="column-input max-width-400">
                                    <el-form-item prop="verificationCode">
                                        <el-input class="input verification-code-input" v-model="form.verificationCode" :disabled="stepTwoDisabled" placeholder="本人確認コード"></el-input>
                                        <span v-if="!codeSent" class="d-inline-block small-text">先に本人確認コードを送信してください</span>
                                    </el-form-item>
                                </td>
                            </tr>
                        </table>
                        <div class="iv-step__action text-center">
                            <div v-if="isComplete">
                                <div class="complete-wording">
                                    変更が完了しました。
                                </div>
                            </div>
                            <div v-else>
                                <div class="confirm-wording">
                                    上記内容で変更します。よろしいですか？
                                </div>
                                <br>
                                <el-button type="primary" @click="handleChangeLoginId" :disabled="stepTwoDisabled">変更する</el-button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </el-form>
    </div>
</template>

<script>
import memberApi from '@/api/member/member-client.js'
import verificationCodeCooldownMixin from '@/mixins/verificationCodeCooldownMixin'

// Used by rules(). The send button checks step ① through the same rules (validateStepOne).
const EMAIL_PATTERN = /^[a-zA-Z0-9]{1}[a-zA-Z0-9_.-]*@{1}(([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3})|([a-zA-Z0-9_.-]{1,}\.[a-zA-Z0-9]{2,}))$/
const MOBILE_PATTERN = /^\d{3}-\d{4}-\d{4}$/

export default {
    name: 'memberChangeLoginId',
    mixins: [verificationCodeCooldownMixin],
    data () {
        return {
            form: {
                email: '',
                mobilePhone: '',
                loginIdType: 'email',
                verificationCode: ''
            },
            disabled: false,
            isComplete: false,
            // Set once a code is sent successfully in this page visit; unlocks step ②.
            codeSent: false
        }
    },
    created () {
        this.loadCurrentContactInfo()
    },
    computed: {
        stepTwoDisabled () {
            return this.disabled || !this.codeSent
        },
        isEmailLoginIdRequired () {
            return this.form.loginIdType === 'email'
        },
        isMobileLoginIdRequired () {
            return this.form.loginIdType === 'mobile'
        },
        rules () {
            const emailRules = []
            const mobilePhoneRules = []

            if (this.form.loginIdType === 'email') {
                emailRules.push(
                    { required: true, message: 'メールアドレスを入力してください', trigger: 'blur' },
                    { pattern: EMAIL_PATTERN, message: '正しいメールアドレスを入力してください', trigger: 'blur' }
                )
            } else if (this.form.email) {
                emailRules.push(
                    { pattern: EMAIL_PATTERN, message: '正しいメールアドレスを入力してください', trigger: 'blur' }
                )
            }

            if (this.form.loginIdType === 'mobile') {
                mobilePhoneRules.push(
                    { required: true, message: '携帯電話番号を入力してください', trigger: 'blur' },
                    { pattern: MOBILE_PATTERN, message: '携帯電話番号は半角数字（ハイフンあり）で入力してください', trigger: 'blur' }
                )
            } else if (this.form.mobilePhone) {
                mobilePhoneRules.push(
                    { pattern: MOBILE_PATTERN, message: '携帯電話番号は半角数字（ハイフンあり）で入力してください', trigger: 'blur' }
                )
            }

            return {
                email: emailRules,
                mobilePhone: mobilePhoneRules,
                verificationCode: [
                    { required: true, message: '本人確認コードを入力してください', trigger: 'blur' }
                ]
            }
        },
        newLoginId () {
            if (this.form.loginIdType === 'mobile') {
                return this.form.mobilePhone
            }
            return this.form.email
        },
        // Display only: a phone login ID is shown without hyphens (09012345678). newLoginId itself
        // keeps the form's hyphenated value, since the OTC-send/submit validation and payload use it.
        newLoginIdDisplay () {
            if (this.form.loginIdType === 'mobile') {
                return (this.newLoginId || '').replace(/-/g, '')
            }
            return this.newLoginId
        },
        // Which column (email/phone) serves as login_id can never be switched - only its value
        // may change. Shown as a memo under 新しいログインID; form.loginIdType itself is fixed by
        // loadCurrentContactInfo.
        loginIdTypeLabel () {
            return this.form.loginIdType === 'mobile' ? '携帯電話番号' : 'メールアドレス'
        }
    },
    methods: {
        handleSendVerificationCode () {
            if (this.verificationCodeCooldown > 0) {
                this.$message({
                    message: '本人確認コードは30秒に1回のみ送信できます',
                    type: 'error',
                    duration: 3000
                })
                return
            }
            // Step ① must be complete before a code is sent (same as 本人確認, #13): the form's own
            // rules for email/mobilePhone, so errors show inline. The login-ID field's rules
            // include required + shape, so this also covers the old manual pattern check.
            return this.validateStepOne().then(valid => {
                if (!valid) {
                    return
                }
                return this.sendVerificationCode()
            })
        },
        validateStepOne () {
            const form = this.$refs['memberChangeLoginId']
            return Promise.all(['email', 'mobilePhone'].map(prop => new Promise(resolve => {
                form.validateField(prop, errorMessage => resolve(!errorMessage))
            }))).then(results => results.every(Boolean))
        },
        sendVerificationCode () {
            // IDENTITY_UPDATE requires an explicit targetIdentifier - the value of whichever
            // field (email or mobilePhone) is the selected login_id type.
            return memberApi.sendOtc({ purpose: 'IDENTITY_UPDATE', targetIdentifier: this.newLoginId }).then(() => {
                this.codeSent = true
                this.$message({
                    message: 'お客様に本人確認コードを送信しました',
                    type: 'success',
                    duration: 3000
                })
                this.startVerificationCodeCooldown()
            }).catch(err => {
                this.alertMessage(err.response.data.message, 'エラー')
            })
        },
        handleChangeLoginId () {
            this.$refs['memberChangeLoginId'].validate((valid) => {
                if (valid) {
                    if (!this.newLoginId) {
                        this.alertMessage('ログインIDとして使用する項目を選択してください。', '入力情報エラー')
                        return
                    }
                    this.disabled = true
                    const dataRequest = {
                        loginIdType: this.form.loginIdType,
                        email: this.form.email,
                        mobilePhone: this.form.mobilePhone,
                        verificationCode: this.form.verificationCode
                    }
                    memberApi.updateIdentityContact(dataRequest).then(() => {
                        this.$msgbox({
                            title: 'ログインIDの変更が完了しました。',
                            showClose: true,
                            center: true,
                            confirmButtonClass: 'btn-msg-box'
                        })
                        setTimeout(() => { this.$msgbox.close() }, 2000)
                        this.isComplete = true
                    }).catch(err => {
                        this.disabled = false
                        // Either way the old code is no longer usable - wrong is still wrong,
                        // right has already been consumed.
                        this.form.verificationCode = ''
                        this.alertMessage(err.response.data.message, '入力情報エラー')
                    })
                } else {
                    this.alertMessage('入力情報を再度ご確認ください。', '入力情報エラー')
                }
            })
        },
        async loadCurrentContactInfo () {
            const member = await memberApi.getLoginUserInfo()
            this.form.email = member.email || ''
            this.form.mobilePhone = member.mobilePhone || ''
            // member.email is a raw, unnormalized value and can't be reliably compared against
            // login_id directly - the +81 prefix is the reliable signal for phone-type login_id.
            this.form.loginIdType = (member.loginId || '').startsWith('+81') ? 'mobile' : 'email'
        },
        alertMessage (message, title) {
            this.$alert(message, title, {
                confirmButtonText: this.$t('commons.ok')
            })
        }
    }
}
</script>

<style>
    @import 'member_register.css';
    @import '../../../../assets/css/guided_steps.css';
    .member-edit {
        padding-bottom: 2em;
    }
    @media screen and (min-width:769px) {
        .form th {
            width: 30%;
        }
    }
    .login-id-display input {
        background-color: #f5f5f5 !important;
        color: #333 !important;
        cursor: default !important;
    }
</style>
<style scoped>
/* Same look as the AIユイゴン Well-B notice in Login.vue/ForgotPassword.vue (login.css, scoped there). */
.login-sso-notice {
    background: #eef7fb;
    border: 1px solid #9ccde0;
    border-left: 4px solid #009cad;
    border-radius: 4px;
    margin-bottom: 1.25em;
    padding: 0.85em 1em;
}
.login-sso-notice__title {
    display: flex;
    align-items: center;
    gap: 0.35em;
    margin: 0 0 0.4em;
    color: #007a8a;
    font-size: 15px;
    font-weight: bold;
    line-height: 1.4;
}
.login-sso-notice__icon {
    font-size: 18px;
}
.login-sso-notice__text {
    margin: 0;
    color: #333;
    font-size: 14px;
    font-weight: bold;
    line-height: 1.6;
}
</style>
